<template>
  <section id="app" class="parent-dom __vooglam_app">
    <ClientOnly>
      <scroll :scrollTopY="scrollTop" @scroll="handleScroll">
        <HeaderSection ref="HeaderSectionRef" :inErrorPage="true" />
        <NotFound pageType="404" :inErrorPage="true" />
        <FooterSection id="main-footer" class="main-footer" />
      </scroll>
    </ClientOnly>
  </section>
</template>

<script setup lang="ts">
import api from '@/api';
import type { PcCategoryNavigation } from '@/api/types/nav';
import HeaderSection from '@/components/layout/HeaderSection.vue';
import { useStore } from '@/store';
import NotFound from '@/views/error/NotFound.vue';
import { provide, ref } from 'vue';

const HeaderSectionRef = ref<InstanceType<typeof HeaderSection> | null>(null);
type LayoutJsonValues = Record<string, any> & {
  pc_category_navigation?: PcCategoryNavigation | null;
  product_lens_config?: Record<string, any>;
  sgtm_pixel_config?: Record<string, any>;
};

const store = useStore();
const { SET_BFF_CSR_DATA, SET_BFF_SSR_DATA } = store;

const scrollTop = ref(0);
const handleScroll = (top: number): void => {
  scrollTop.value = top;
};

// 与 default 布局一致：子组件（Header/Footer/MagicJump）会 inject，错误页需占位避免运行时异常
provide('showEggPop', () => {});
provide('openInstadesk', () => {});
provide('closeInstadesk', () => {});
provide('showAppPop', () => {});
provide('showNormalRegisteredPop', () => {});
provide('changeFreeGiftPop', () => {});

const fetchCsrData = async () => {
  const { data: bffRes } = await api.commonTs.bffPcGlobal({
    data: {
      ding_tong: true,
      logo: true,
      default_search: true,
      search_recommended: true,
      search_faq: true,
      foot_nav: true,
      currency: true,

      json_values: ['contact_chat_open_time', 'pc_category_navigation'],
    },
  });
  if (bffRes) {
    const _jsonvalues = bffRes.json_values as LayoutJsonValues | undefined;
    SET_BFF_SSR_DATA({
      ding_tong: bffRes.ding_tong || null,
      logo: bffRes.logo || null,
      top_nav: Array.isArray(_jsonvalues?.pc_category_navigation)
        ? _jsonvalues.pc_category_navigation
        : null,
      default_search: bffRes.default_search || [],
      foot_nav: bffRes.foot_nav || null,
    });
    const jsonValues = bffRes.json_values as Record<string, any> | undefined;
    SET_BFF_CSR_DATA({
      contact_chat_open_time: jsonValues?.contact_chat_open_time || null,
      search_recommended: bffRes.search_recommended || {},
      search_faq: bffRes.search_faq || {},
    });
    const currencyList = bffRes.currency;
    store.setCurrencyList(Array.isArray(currencyList) ? currencyList : []);
  }
};

onMounted(async () => {
  await fetchCsrData();
  nextTick(() => {
    HeaderSectionRef.value?.updateNavScreenWidth();
  });
});
</script>

<!-- 在这里引入一些公共css，避免公共css打入 HTML 文档 -->
<style lang="scss" src="./assets/style/index.scss"></style>
<!-- iconfont.css 单独引入，不使用 lang="scss"，避免被 SCSS 预处理器处理 Unicode 转义序列 -->
<style src="./assets/icon/iconfont.css"></style>
<style lang="scss">
html {
  /* stylelint-disable-next-line selector-id-pattern */
  #__vooglam {
    width: 100%;
    height: 100%;
  }
}
body,
html {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;

  --fs-24: 24px;
  --fs-18: 18px;
  --fs-16: 16px;
  --fs-14: 14px;
  --fs-12: 12px;
  &.no-onesignel {
    #onesignal-slidedown-container {
      display: none;
    }
  }
}

// 隐藏谷歌防护 reCAPTCHA V3自动添加的信息
.grecaptcha-badge {
  display: none !important;
}

/* 隐藏顶部网页翻译条 */
.skiptranslate,
#google_translate_element {
  display: none !important;
}
#app {
  padding-top: 66px;
  width: 100%;
  height: 100%;
  font-size: 14px;
  font-family: $f-pr;
  color: #080506;
  transition: all 0.2s;
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
</style>
